import { useEffect, useState } from 'react';
import ApexCharts from 'apexcharts';
import { SQUAD_CHART_CAPTURE_SCALE } from './squadPortfolioChartUtils';
import { SQUAD_FONT_FILES, SQUAD_FONT_STACK } from './templateConstants';

/**
 * Segoe UI in the two ApexCharts charts (NAV p. 1, rolling p. 3), like the rest of the
 * factsheet (SQUAD 30.10.2026).
 *
 * Both charts reach the PDF as PNGs. ApexCharts' own export serialises the SVG and draws
 * it through an <img>, and an SVG image can neither use the page's fonts nor fetch any,
 * so the chart text fell back to Helvetica/Arial. captureSquadChart embeds the font files
 * as data URIs in the serialised SVG instead.
 *
 * The live charts use the same files through document.fonts, so ApexCharts lays them out
 * (y-axis width, which value labels fit their bar) with the font the PNG shows. That only
 * holds if the font is there before the first draw: the charts mount once it has settled
 * (useSquadChartFontStatus).
 *
 * An own family name: registering "SegoeUI" itself would switch every text of the editor
 * preview, which uses the system font today.
 */
export const SQUAD_CHART_FONT_FAMILY = 'SquadChartSegoeUI';

/** ApexCharts `chart.fontFamily` — axis labels, value labels and legend inherit it. */
export const SQUAD_CHART_FONT_STACK = `${SQUAD_CHART_FONT_FAMILY}, ${SQUAD_FONT_STACK}`;

/**
 * Regular for axes and legend. The rolling value labels ask for weight 600, which resolves
 * to the next heavier face: Bold, like every other value label of the factsheet.
 */
const SQUAD_CHART_FONT_FACES = [
  { weight: '400', url: SQUAD_FONT_FILES.regular },
  { weight: '700', url: SQUAD_FONT_FILES.bold },
] as const;

/**
 * Longest wait for the two font files (1.9 MB together). Past it the charts draw and export
 * with the fallback stack: a stalled request must not hold up Preview or Publish.
 */
export const SQUAD_CHART_FONT_TIMEOUT_MS = 8000;

/** 'loading' until the first attempt settles. A retry can still turn 'fallback' into 'loaded'. */
export type SquadChartFontStatus = 'loading' | 'loaded' | 'fallback';

let fontStatus: SquadChartFontStatus = 'loading';
const statusListeners = new Set<(status: SquadChartFontStatus) => void>();
/** The latest attempt — in flight, or settled with the rules (null: it failed). */
let fontCssPromise: Promise<string | null> | null = null;
let lastAttemptFailed = false;

function setFontStatus(next: SquadChartFontStatus) {
  if (next === fontStatus) return;
  fontStatus = next;
  statusListeners.forEach((listener) => listener(next));
}

function toFontDataUri(buffer: ArrayBuffer): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^;,]*/, 'data:font/ttf'));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(new Blob([buffer]));
  });
}

function fetchFontFaces(signal?: AbortSignal) {
  return Promise.all(
    SQUAD_CHART_FONT_FACES.map(async ({ weight, url }) => {
      const res = await fetch(url, { signal });
      if (!res.ok) throw new Error(`${url}: HTTP ${res.status}`);
      const buffer = await res.arrayBuffer();
      const face = new FontFace(SQUAD_CHART_FONT_FAMILY, buffer, { weight });
      await face.load();
      return { weight, face, src: await toFontDataUri(buffer) };
    }),
  );
}

async function attemptFontLoad(): Promise<string | null> {
  if (typeof FontFace === 'undefined' || typeof document === 'undefined' || !document.fonts) {
    lastAttemptFailed = true;
    setFontStatus('fallback');
    return null;
  }
  const controller = typeof AbortController === 'undefined' ? undefined : new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const loaded = await Promise.race([
      fetchFontFaces(controller?.signal),
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => {
          controller?.abort();
          reject(new Error(`no font within ${SQUAD_CHART_FONT_TIMEOUT_MS} ms`));
        }, SQUAD_CHART_FONT_TIMEOUT_MS);
      }),
    ]);
    // All faces or none: Regular alone would let the preview and the PNG disagree on Bold.
    loaded.forEach(({ face }) => document.fonts.add(face));
    setFontStatus('loaded');
    return loaded
      .map(
        ({ weight, src }) =>
          `@font-face{font-family:'${SQUAD_CHART_FONT_FAMILY}';font-style:normal;` +
          `font-weight:${weight};src:url(${src}) format('truetype');}`,
      )
      .join('');
  } catch (error) {
    console.warn('SQUAD chart font not loaded, the charts use the fallback font:', error);
    lastAttemptFailed = true;
    setFontStatus('fallback');
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Loads the chart font: registers it for the live charts and returns the @font-face rules
 * for the capture. Null when it fails or times out; the charts then draw and export with
 * the fallback stack, as before. A success is kept for the page, a failure is not: the
 * next call tries again (captureAll calls it once per Preview and Publish).
 */
export function loadSquadChartFont(): Promise<string | null> {
  if (!fontCssPromise || lastAttemptFailed) {
    lastAttemptFailed = false;
    fontCssPromise = attemptFontLoad();
  }
  return fontCssPromise;
}

/**
 * The chart font's status. Mount the charts once it is no longer 'loading', and key them on
 * it: when a retry loads the font after a fallback, they re-mount and ApexCharts measures
 * again with the font the capture embeds.
 */
export function useSquadChartFontStatus(): SquadChartFontStatus {
  const [status, setStatus] = useState(fontStatus);

  useEffect(() => {
    statusListeners.add(setStatus);
    // It may have changed between render and effect.
    setStatus(fontStatus);
    if (fontStatus !== 'loaded') loadSquadChartFont();
    return () => {
      statusListeners.delete(setStatus);
    };
  }, []);

  return status;
}

/** The font rules go first into the exported SVG: an SVG image only has what it carries. */
export function withSquadChartFont(svg: string, fontCss: string): string {
  return svg.replace(
    /<svg\b[^>]*>/,
    (open) => `${open}<defs><style type="text/css">${fontCss}</style></defs>`,
  );
}

/** The parts of an ApexCharts 4 instance the capture uses (not all in its typings). */
type ApexChartForCapture = {
  w: {
    config: { chart: { background?: string } };
    globals: { svgWidth: number; dom: { elWrap: HTMLElement } };
  };
  exports: { getSvgString: (scale?: number) => Promise<string> };
  dataURI: (options: { scale: number }) => Promise<{ imgURI?: string }>;
};

async function rasteriseWithFont(
  chart: ApexChartForCapture,
  fontCss: string,
  scale: number,
): Promise<string> {
  const { w } = chart;
  const svg = withSquadChartFont(await chart.exports.getSvgString(scale), fontCss);

  // Canvas size and background exactly as ApexCharts' dataURI, so the PNG keeps its size.
  const canvas = document.createElement('canvas');
  canvas.width = w.globals.svgWidth * scale;
  canvas.height = parseInt(w.globals.dom.elWrap.style.height, 10) * scale;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No 2D canvas context');
  const background = w.config.chart.background;
  ctx.fillStyle = !background || background === 'transparent' ? '#fff' : background;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const img = new Image();
  img.src = `data:image/svg+xml,${encodeURIComponent(svg)}`;
  // Resolves once the image can be drawn, its embedded fonts included.
  await img.decode();
  ctx.drawImage(img, 0, 0);
  return canvas.toDataURL('image/png');
}

/**
 * PNG of a SQUAD ApexCharts chart with Segoe UI embedded; replaces
 * `ApexCharts.exec(id, 'dataURI', { scale })`.
 *
 * The SVG string still comes from ApexCharts and clones the live DOM at this moment, so
 * the rolling value labels keep the places squadRollingDataLabels moved them to.
 */
export async function captureSquadChart(
  chartId: string,
  scale: number = SQUAD_CHART_CAPTURE_SCALE,
): Promise<string | undefined> {
  // The latest attempt, no retry per chart: captureAll already retried once before.
  const fontCss = await (fontCssPromise ?? loadSquadChartFont());
  const chart = ApexCharts.getChartByID(chartId) as unknown as ApexChartForCapture | undefined;
  if (!chart) return undefined;
  if (fontCss) {
    try {
      return await rasteriseWithFont(chart, fontCss, scale);
    } catch (error) {
      console.error(`SQUAD chart capture with font failed (${chartId}):`, error);
    }
  }
  // Without the font: ApexCharts' own export, so the PDF still gets the chart.
  try {
    const res = await chart.dataURI({ scale });
    return res?.imgURI;
  } catch {
    return undefined;
  }
}
